/**
 * Brand colors for the admin portal.
 *
 * Primary is driven by REACT_APP_THEME_COLOR (build-time CRA env).
 * When unset or invalid, production red is used so merges/deploys never
 * accidentally change production branding.
 *
 * Staging: set REACT_APP_THEME_COLOR=#1976D2 (or another blue) on the
 * staging host only. Leave production unset (or set #e90000 explicitly).
 */

export const DEFAULT_BRAND_PRIMARY = '#e90000'
export const DEFAULT_BRAND_HOVER = '#c00000'
export const DEFAULT_BRAND_GRADIENT_END = '#e01111'
export const DEFAULT_BRAND_LIGHT = '#f43f5e'
export const DEFAULT_BRAND_DARK = '#990000'

/** Recommended staging primary (distinct from production red). */
export const STAGING_BRAND_PRIMARY = '#1976D2'

function normalizeHex(value) {
  if (value == null || typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) {
    return withHash.toLowerCase()
  }
  if (/^#[0-9A-Fa-f]{8}$/.test(withHash)) {
    return withHash.slice(0, 7).toLowerCase()
  }
  return null
}

function hexToRgb(hex) {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

function rgbToHex({ r, g, b }) {
  const to = (n) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

/** factor < 1 darkens; factor > 1 lightens */
function adjustBrightness(hex, factor) {
  const { r, g, b } = hexToRgb(hex)
  return rgbToHex({ r: r * factor, g: g * factor, b: b * factor })
}

/**
 * @param {string | undefined | null} primaryOverride
 * @returns {{
 *   primary: string,
 *   hover: string,
 *   gradientEnd: string,
 *   light: string,
 *   dark: string,
 *   contrastText: string,
 *   gradient: string,
 * }}
 */
export function createBrandColors(primaryOverride) {
  const fromEnv = normalizeHex(primaryOverride)
  const primary = fromEnv || DEFAULT_BRAND_PRIMARY.toLowerCase()
  const usingDefaultRed = primary === DEFAULT_BRAND_PRIMARY.toLowerCase()

  if (usingDefaultRed) {
    return {
      primary: DEFAULT_BRAND_PRIMARY,
      hover: DEFAULT_BRAND_HOVER,
      gradientEnd: DEFAULT_BRAND_GRADIENT_END,
      light: DEFAULT_BRAND_LIGHT,
      dark: DEFAULT_BRAND_DARK,
      contrastText: '#fff',
      gradient: `linear-gradient(135deg, ${DEFAULT_BRAND_PRIMARY} 0%, ${DEFAULT_BRAND_GRADIENT_END} 100%)`,
    }
  }

  const hover = adjustBrightness(primary, 0.82)
  const gradientEnd = adjustBrightness(primary, 0.92)
  const light = adjustBrightness(primary, 1.28)
  const dark = adjustBrightness(primary, 0.7)

  return {
    primary,
    hover,
    gradientEnd,
    light,
    dark,
    contrastText: '#fff',
    gradient: `linear-gradient(135deg, ${primary} 0%, ${gradientEnd} 100%)`,
  }
}

export const brandColors = createBrandColors(process.env.REACT_APP_THEME_COLOR)
